/**
 * useTeleopController - Shared hook for Hz-based teleop control
 * 
 * This hook provides a unified teleop control system with:
 * - Configurable command rate (Hz)
 * - Keyboard input handling (WASD + Arrow keys)
 * - Emergency stop functionality
 * - Automatic stop commands when keys are released
 * 
 * Used by: SlamViewer, RobotControl
 */

import { useState, useEffect, useRef, useCallback } from 'react';

interface TeleopControllerOptions {
  robotSerial: string | undefined;
  isOnline: boolean;
  sendTeleop: (serial: string, linear: number, angular: number) => void;
  commandRateHz: number;
  maxSpeed: number;
  enabled?: boolean;  // Optional: allows disabling teleop (e.g., when not on control tab)
}

interface TeleopControllerReturn {
  activeKeys: Set<string>;
  handleKeyDown: (key: string) => void;
  handleKeyUp: (key: string) => void;
  emergencyStop: () => void;
  lastCommand: { linear: number; angular: number };
  isActive: boolean;
}

export const useTeleopController = ({
  robotSerial,
  isOnline,
  sendTeleop,
  commandRateHz,
  maxSpeed,
  enabled = true,
}: TeleopControllerOptions): TeleopControllerReturn => {
  const [activeKeys, setActiveKeys] = useState<Set<string>>(new Set());
  // v6.3.32: the send loop reads keys through a ref so the interval isn't
  // torn down and recreated on every keypress (activeKeys was in the effect
  // deps — the command rate jittered on each key event).
  const activeKeysRef = useRef(activeKeys);
  activeKeysRef.current = activeKeys;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const stopCountRef = useRef(0);
  const lastCommandRef = useRef({ linear: 0, angular: 0 });

  // Recreate interval when Hz or other dependencies change
  useEffect(() => {
    if (!robotSerial || !isOnline || !enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const intervalMs = 1000 / commandRateHz;

    const sendCommand = () => {
      const keys = activeKeysRef.current;
      let linear = 0;
      let angular = 0;

      // Calculate movement from active keys
      if (keys.has('w') || keys.has('arrowup')) linear += 1;
      if (keys.has('s') || keys.has('arrowdown')) linear -= 1;
      if (keys.has('a') || keys.has('arrowleft')) angular += 1;
      if (keys.has('d') || keys.has('arrowright')) angular -= 1;

      // Emergency stop (space key)
      if (keys.has(' ')) {
        linear = 0;
        angular = 0;
      }

      // Scale by max speed
      linear *= maxSpeed;
      angular *= maxSpeed * 2;  // Angular is typically faster

      if (keys.size > 0) {
        // Keys pressed - send command and reset stop counter
        sendTeleop(robotSerial, linear, angular);
        lastCommandRef.current = { linear, angular };
        stopCountRef.current = 0;
      } else if (
        lastCommandRef.current.linear !== 0 ||
        lastCommandRef.current.angular !== 0 ||
        stopCountRef.current < 3
      ) {
        // No keys pressed - send stop command 3 times to ensure robot stops
        sendTeleop(robotSerial, 0, 0);
        lastCommandRef.current = { linear: 0, angular: 0 };
        stopCountRef.current++;
      }
    };

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Create new interval with current Hz
    intervalRef.current = setInterval(sendCommand, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [robotSerial, isOnline, commandRateHz, maxSpeed, enabled, sendTeleop]);

  // v6.3.32 SAFETY: release all keys + send stop when the window loses focus
  // or the tab is hidden. Previously alt-tabbing mid-drive left keys latched
  // in activeKeys and the robot kept driving on the last command.
  useEffect(() => {
    const stopAll = () => {
      setActiveKeys(new Set());
      if (robotSerial && isOnline) {
        sendTeleop(robotSerial, 0, 0);
        sendTeleop(robotSerial, 0, 0);
      }
      lastCommandRef.current = { linear: 0, angular: 0 };
    };
    const onVisibility = () => { if (document.hidden) stopAll(); };
    window.addEventListener('blur', stopAll);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', stopAll);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [robotSerial, isOnline, sendTeleop]);

  const handleKeyDown = useCallback((key: string) => {
    setActiveKeys((prev) => new Set(prev).add(key.toLowerCase()));
  }, []);

  const handleKeyUp = useCallback((key: string) => {
    setActiveKeys((prev) => {
      const next = new Set(prev);
      next.delete(key.toLowerCase());
      return next;
    });
  }, []);

  const emergencyStop = useCallback(() => {
    setActiveKeys(new Set());
    if (robotSerial && isOnline) {
      // Send stop 3 times immediately for reliability
      sendTeleop(robotSerial, 0, 0);
      sendTeleop(robotSerial, 0, 0);
      sendTeleop(robotSerial, 0, 0);
    }
    lastCommandRef.current = { linear: 0, angular: 0 };
    stopCountRef.current = 3;
  }, [robotSerial, isOnline, sendTeleop]);

  return {
    activeKeys,
    handleKeyDown,
    handleKeyUp,
    emergencyStop,
    lastCommand: lastCommandRef.current,
    isActive: activeKeys.size > 0,
  };
};

export default useTeleopController;
